import MonitorNetwork from "@components/MonitorNetwork";
import PlatformMetadataComponent from "@components/PlatformMetadataComponent";
import WhiteLabellingContext from "@context/WhitelabellingContext";
import TailwindCssContext from "@context/TailwindCssContext";
import styles from "@styles/LumenorePlatform.module.scss";
import { getEnvConstantsValue } from "@utility/environmentVariables";
import NextApp, { type AppContext, type AppProps } from "next/app";
import { WHITE_LABELLING_CONTENT } from "@constants/constants";
import { computeWhitelabellingContentRequestPayload, fetchWhiteLabellingContent } from "@utility/whitelabellingUtility";
import { DM_Sans } from 'next/font/google';
import { useRouter } from "next/router";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import "@styles/global.scss";

const fontFamily = DM_Sans({
  subsets: ['latin'],
  variable: '--font-family', // this creates a CSS variable
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  display: 'swap',
});

type NextPageWithLayout = AppProps["Component"] & {
  layoutProps?: {
    disableTailwindStyles?: boolean
  },
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  whitelabellingContent?: WhiteLabellingContent;
  whitelabellingContentRequestPayload?: WhiteLabellingContentRequestPayload;
};

const renderTailwindStyles = async (disableTailwindStyles: boolean, setCssLoading: (value: boolean) => void, setTailwindCssLoaded: (value: boolean) => void) => {
  if (disableTailwindStyles) {
    setCssLoading(false)
    return false
  }
  else {
    setCssLoading(true);
    try {
      // eslint-disable-next-line
      // @ts-ignore
      await import("@styles/tailwindStyles.scss");
      setTailwindCssLoaded(true);
      setCssLoading(false);
      return true
    }
    catch (error) {
      console.log("Error while import tailwind", error)
      setTailwindCssLoaded(false);
      setCssLoading(false);
      return false
    }
  }
}

const cleanHashFromUrl = (url: string) => {
  const cleanUrl = url.replaceAll("#", "");
  window.location.replace(cleanUrl);
}

export default function App({ Component, pageProps, whitelabellingContent = WHITE_LABELLING_CONTENT, whitelabellingContentRequestPayload = { domain: null, organizationName: null } }: AppPropsWithLayout) {
  const [cssLoading, setCssLoading] = useState(true)
  const [tailwindCssLoaded, setTailwindCssLoaded] = useState(false)

  const router = useRouter();
  const isRootRoute = router.pathname === "/";
  const defaultProductPage = Boolean(getEnvConstantsValue("DEFAULT_PRODUCT_PAGE")) && getEnvConstantsValue("DEFAULT_PRODUCT_PAGE") !== "false";
  const getLayout = Component.getLayout ?? ((page) => page);
  const { disableTailwindStyles: disableTailwindStylesFromPage = false } = Component.layoutProps || {};
  const showSSOAuthPage = pageProps?.defaultPage === "ssoauth";
  const disableTailwindStyles = disableTailwindStylesFromPage || (isRootRoute && defaultProductPage && !showSSOAuthPage);

  useEffect(() => {
    const url = (window?.location?.href || "");
    const hasHash = url.includes("#");
    renderTailwindStyles(disableTailwindStyles, setCssLoading, setTailwindCssLoaded);
    if (url && hasHash) {
      cleanHashFromUrl(url);
    }
    else {
      /* do nothing */
    }
  }, [])

  const loadTailwind = () => renderTailwindStyles(false, setCssLoading, setTailwindCssLoaded);

  const contextValue = useMemo(() => ({ tailwindCssLoaded, loadTailwind }), [tailwindCssLoaded, loadTailwind]);

  return (
    <WhiteLabellingContext.Provider value={whitelabellingContent}>
      <PlatformMetadataComponent whitelabellingContent={whitelabellingContent} whitelabellingContentRequestPayload={whitelabellingContentRequestPayload} />
      <div data-testid={"app-test"} className={`${fontFamily.className} ${styles.lumenorePlatformLayoutContainer}`}>
        <MonitorNetwork />
        <TailwindCssContext.Provider value={contextValue}>
          {cssLoading ? null : getLayout(<Component {...pageProps} whitelabellingContent={whitelabellingContent} />)}
        </TailwindCssContext.Provider>
      </div>
    </WhiteLabellingContext.Provider>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  // Resolve whitelabelling first; page/authentication loading must not race this prerequisite.
  const whitelabellingContentRequestPayload = computeWhitelabellingContentRequestPayload(appContext);
  console.log("[WhiteLabelling] Computed request payload:", whitelabellingContentRequestPayload);
  const whitelabellingContent = await fetchWhiteLabellingContent(whitelabellingContentRequestPayload);
  const appProps = await NextApp.getInitialProps(appContext);
  return { ...appProps, whitelabellingContent, whitelabellingContentRequestPayload };
};
